/**
 * The student summary experiment's shared contract: which summary type and
 * twist group each section is in, the analytics events the guide logs, the
 * section leader's thumbs, the suggested twists, and the PromptNCE and
 * adaptive runs. The pipeline (`apps/functions/src/guideSummaries`,
 * `apps/functions/src/summaryEval`), the CLI and the staff dashboards all
 * read and write these shapes.
 *
 * Design: `docs/plans/student-summaries/experiment-design.md`.
 */

import {z} from 'zod';

/**
 * The live summary types. Each section gets one for the whole course: A is
 * the CIP6 prompt and B is Recurrent + Rubric (`config.typeMethods` names
 * each type's method). An adaptive third type was dropped for CIPX because
 * the adaptive step only edits single-call methods.
 */
export const SUMMARY_TYPES = ['A', 'B'] as const;

/** A live summary type. */
export type SummaryType = (typeof SUMMARY_TYPES)[number];

/** How a section's suggested twists are chosen. */
export const TWIST_GROUPS = ['summary', 'random'] as const;

/** How a section's suggested twists are chosen. */
export type TwistGroup = (typeof TWIST_GROUPS)[number];

/**
 * One section's experiment assignment, at
 * `summaryPipeline/{courseId}/assignments/{sectionId}`.
 */
export const SectionAssignmentSchema = z.object({
  sectionId: z.string(),
  summaryType: z.enum(SUMMARY_TYPES),
  /** Absent for courses without twists (PAI), so no twists are suggested. */
  twistGroup: z.enum(TWIST_GROUPS).optional(),
  /** The block the section was randomized within, e.g. `tue15|6-7|new`. */
  stratum: z.string(),
  /** Section leader uid at assignment time; summaries follow them to TeachNow. */
  sectionLeaderId: z.string().optional(),
  assignedAtMs: z.number(),
  /** Which run of the assignment script wrote this. */
  assignmentRunId: z.string(),
});

/** One section's experiment assignment. */
export type SectionAssignment = z.infer<typeof SectionAssignmentSchema>;

/**
 * Path of a section's assignment doc.
 *
 * @param params.courseId The course.
 * @param params.sectionId The section.
 * @return The document path.
 * @throws Never.
 */
export function getSectionAssignmentPath({
  courseId,
  sectionId,
}: {
  courseId: string;
  sectionId: string;
}): string {
  return `summaryPipeline/${courseId}/assignments/${sectionId}`;
}

/**
 * Path of the assignments collection.
 *
 * @param params.courseId The course.
 * @return The collection path.
 * @throws Never.
 */
export function getSectionAssignmentsPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `summaryPipeline/${courseId}/assignments`;
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

/**
 * What a staff member or section leader did with summaries. Written by the
 * browser, so every field is untrusted beyond what the rules check.
 */
export const SUMMARY_ANALYTICS_EVENT_KINDS = [
  /** Opened the summaries tab. */
  'summariesTabView',
  /** Left the summaries tab; `durationMs` is how long it was open and visible. */
  'summariesTabLeave',
  /** Opened the Twists Plan tab. */
  'twistsTabView',
  /** Left the Twists Plan tab. */
  'twistsTabLeave',
  /** Pressed "Get summaries now". */
  'refreshClick',
  /** Rated one student's summary; `value` is `up`, `down` or `clear`. */
  'studentThumb',
  /** Opened a suggested twist from the suggested twists tab. */
  'suggestedTwistClick',
  /** Expanded a student's summary text, where the UI truncates it. */
  'studentExpand',
  /** Saw the section summary card on the section page. */
  'sectionPageSummaryView',
  /** Followed the section page card's link to the guide's summaries tab. */
  'sectionPageSummaryClick',
] as const;

/** What a person did with summaries. */
export type SummaryAnalyticsEventKind =
  (typeof SUMMARY_ANALYTICS_EVENT_KINDS)[number];

/**
 * One analytics event, at `summaryAnalytics/{courseId}/events/{autoId}`.
 */
export const SummaryAnalyticsEventSchema = z.object({
  kind: z.enum(SUMMARY_ANALYTICS_EVENT_KINDS),
  /** Who did it; the rules require it to be the signed-in user. */
  uid: z.string(),
  /** `sl` for the section's leader, `staff` for TA and above. */
  viewerRole: z.enum(['sl', 'staff']),
  sectionId: z.string(),
  sectionWeek: z.number().int().positive(),
  /** Client clock, epoch ms. The dashboard trusts it only for display. */
  clientAtMs: z.number(),
  /** For `*Leave` events. */
  durationMs: z.number().nonnegative().optional(),
  /** The student the event is about, for per-student events. */
  studentId: z.string().optional(),
  /** Thumb value, or the twist id for `suggestedTwistClick`. */
  value: z.string().max(200).optional(),
  /** Whether section leaders could see summaries at the time. */
  isVisibleToLeaders: z.boolean().optional(),
  /** For `studentThumb`: the run doc of the summary rated. */
  runId: z.string().max(128).optional(),
  /** For `studentThumb`: the method that wrote the summary rated. */
  methodKey: z.string().max(64).optional(),
});

/** One analytics event. */
export type SummaryAnalyticsEvent = z.infer<typeof SummaryAnalyticsEventSchema>;

/**
 * Path of the analytics events collection.
 *
 * @param params.courseId The course.
 * @return The collection path.
 * @throws Never.
 */
export function getSummaryAnalyticsEventsPath({
  courseId,
}: {
  courseId: string;
}): string {
  return `summaryAnalytics/${courseId}/events`;
}

/**
 * A section leader's current thumb on one student's summary, at
 * `summaryFeedback/{courseId}/thumbs/{docId}` with the id from
 * {@link toStudentThumbId}. The latest rating wins; the history is in the
 * analytics events.
 */
export const StudentThumbSchema = z.object({
  uid: z.string(),
  sectionId: z.string(),
  sectionWeek: z.number().int().positive(),
  studentId: z.string(),
  /** Null clears the rating. */
  value: z.enum(['up', 'down']).nullable(),
  /** The run the rated summary came from, so a rating maps to one text. */
  runId: z.string().optional(),
  methodKey: z.string().optional(),
  updatedAtMs: z.number(),
});

/** A section leader's current thumb on one student's summary. */
export type StudentThumb = z.infer<typeof StudentThumbSchema>;

/**
 * Doc id of one rater's thumb on one student's summary for one week.
 *
 * @param params.uid The rater.
 * @param params.sectionId The section.
 * @param params.sectionWeek 1-indexed section week.
 * @param params.studentId The student.
 * @return The doc id.
 * @throws Never.
 */
export function toStudentThumbId({
  uid,
  sectionId,
  sectionWeek,
  studentId,
}: {
  uid: string;
  sectionId: string;
  sectionWeek: number;
  studentId: string;
}): string {
  return `${sectionId}_${String(sectionWeek)}_${studentId}_${uid}`;
}

/**
 * Path of the thumbs collection.
 *
 * @param params.courseId The course.
 * @return The collection path.
 * @throws Never.
 */
export function getStudentThumbsPath({courseId}: {courseId: string}): string {
  return `summaryFeedback/${courseId}/thumbs`;
}

// ---------------------------------------------------------------------------
// Suggested twists
// ---------------------------------------------------------------------------

/** One student's suggested twist. */
export const TwistSuggestionSchema = z.object({
  studentId: z.string(),
  twistId: z.string(),
  twistTitle: z.string(),
  /** The model's reason; empty for the random group. Staff only. */
  reason: z.string().default(''),
  /** Planned turn, from 1; absent on sets written before turn order. */
  turn: z.number().int().positive().optional(),
});

/** One student's suggested twist. */
export type TwistSuggestion = z.infer<typeof TwistSuggestionSchema>;

/**
 * A section week's suggested twists with how they were made, at
 * `sectionsummaries/{courseId}/sections/{sectionId}/twistSuggestions/{week}`.
 * Staff only; the guide doc carries the pairing section leaders see.
 */
export const TwistSuggestionSetSchema = z.object({
  sectionWeek: z.number().int().positive(),
  twistGroup: z.enum(TWIST_GROUPS),
  /** The section problem whose twists were used. */
  problemId: z.string(),
  suggestions: z.array(TwistSuggestionSchema),
  generatedAtMs: z.number(),
  /** Method version of the pairing call; absent for random. */
  methodKey: z.string().optional(),
  /** The pairing prompt and response; absent for random. */
  renderedPrompt: z.string().optional(),
  rawResponse: z.string().optional(),
  costUsd: z.number().nullable().optional(),
  ledgerCallId: z.string().optional(),
  /** Seed of the random pairing, so it can be reproduced. */
  seed: z.string().optional(),
  /**
   * The twist plan the pipeline wrote to the live session from this pairing,
   * kept so section leader edits can be compared with it; absent until one
   * is written.
   */
  plan: z
    .object({
      assignments: z.array(
        z.object({
          uid: z.string(),
          twistId: z.string(),
          turn: z.number().int().positive().optional(),
        }),
      ),
      writtenAtMs: z.number(),
    })
    .optional(),
});

/** A section week's suggested twists. */
export type TwistSuggestionSet = z.infer<typeof TwistSuggestionSetSchema>;

/**
 * Path of a section week's twist suggestion doc.
 *
 * @param params.courseId The course.
 * @param params.sectionId The section.
 * @param params.sectionWeek 1-indexed section week.
 * @return The document path.
 * @throws Never.
 */
export function getTwistSuggestionsPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: string;
  sectionId: string;
  sectionWeek: number;
}): string {
  return `sectionsummaries/${courseId}/sections/${sectionId}/twistSuggestions/${String(sectionWeek)}`;
}

// ---------------------------------------------------------------------------
// PromptNCE evaluation
// ---------------------------------------------------------------------------

/**
 * One PromptNCE evaluation run over one section week, at
 * `summaryEval/{courseId}/runs/{runId}`. Per-student results are in the
 * `items` subcollection.
 */
export const EvalRunSchema = z.object({
  runId: z.string(),
  /** The section week whose summaries are scored. */
  sectionWeek: z.number().int().positive(),
  /** The assignment whose first minutes define each student's future. */
  futureAssnId: z.string(),
  /** Minutes of work on the future assignment that define the future. */
  futureMinutes: z.number().positive(),
  /** Distractor futures per candidate set, plus OTHER. */
  k: z.number().int().positive(),
  /** Method versions scored, e.g. `baseline@1`, `recurrent@1`. */
  methodKeys: z.array(z.string()),
  scorerModel: z.string(),
  /** `live` scores shown summaries; `replay` scores a candidate on saved inputs. */
  mode: z.enum(['live', 'replay']),
  /** For replay runs: the adaptive run it belongs to. */
  adaptiveRunId: z.string().optional(),
  status: z.enum(['running', 'done', 'failed']),
  startedAtMs: z.number(),
  finishedAtMs: z.number().optional(),
  /** Students with a future and at least one scored method. */
  numStudents: z.number().int().nonnegative(),
  /** Mean score per method key. */
  meanScoreByMethod: z.record(z.string(), z.number()),
  /** 95% bootstrap interval per method key, `[low, high]`. */
  ciByMethod: z
    .record(z.string(), z.tuple([z.number(), z.number()]))
    .default({}),
  costUsd: z.number().default(0),
  note: z.string().default(''),
});

/** One PromptNCE evaluation run. */
export type EvalRun = z.infer<typeof EvalRunSchema>;

/** One student's PromptNCE result in a run. */
export const EvalItemSchema = z.object({
  studentId: z.string(),
  sectionId: z.string().optional(),
  /** The section's summary type, for live runs. */
  summaryType: z.enum(SUMMARY_TYPES).optional(),
  /** Conceptual summary of the student's first minutes on the future assignment. */
  future: z.string(),
  /** Candidate futures shown to the scorer, in shown order; the true one included. */
  candidates: z.array(
    z.object({
      label: z.string(),
      text: z.string(),
      isTrue: z.boolean(),
    }),
  ),
  /** Per method: the summary scored and the scorer's probabilities. */
  byMethod: z.record(
    z.string(),
    z.object({
      summary: z.string(),
      /** Probability per candidate label, including `OTHER`. */
      probabilities: z.record(z.string(), z.number()),
      /** Probability on the true future: the PromptNCE score. */
      score: z.number(),
    }),
  ),
});

/** One student's PromptNCE result in a run. */
export type EvalItem = z.infer<typeof EvalItemSchema>;

/**
 * Path of the evaluation runs collection.
 *
 * @param params.courseId The course.
 * @return The collection path.
 * @throws Never.
 */
export function getEvalRunsPath({courseId}: {courseId: string}): string {
  return `summaryEval/${courseId}/runs`;
}

/**
 * Path of one evaluation run's per-student items.
 *
 * @param params.courseId The course.
 * @param params.runId The run.
 * @return The collection path.
 * @throws Never.
 */
export function getEvalItemsPath({
  courseId,
  runId,
}: {
  courseId: string;
  runId: string;
}): string {
  return `summaryEval/${courseId}/runs/${runId}/items`;
}

// ---------------------------------------------------------------------------
// Adaptive method
// ---------------------------------------------------------------------------

/** One candidate prompt in an adaptive step. */
export const AdaptiveCandidateSchema = z.object({
  /** Method key the candidate was stored under, e.g. `adaptive@4`. */
  methodKey: z.string(),
  /** What the optimizer changed and why. */
  edits: z.array(z.object({change: z.string(), reason: z.string()})),
  /** Mean replay score on held-out students. */
  meanScore: z.number().optional(),
  /** Mean paired difference vs the incumbent, with its 95% bootstrap interval. */
  meanDiff: z.number().optional(),
  diffCi: z.tuple([z.number(), z.number()]).optional(),
  medianWords: z.number().optional(),
  costPerUpdateUsd: z.number().optional(),
  /** Guardrail name to pass or fail, e.g. `length`, `cost`, `margin`. */
  guardrails: z.record(z.string(), z.boolean()).default({}),
});

/** One candidate prompt in an adaptive step. */
export type AdaptiveCandidate = z.infer<typeof AdaptiveCandidateSchema>;

/**
 * One weekly adaptive step, at `summaryPipeline/{courseId}/adaptiveRuns/{runId}`.
 */
export const AdaptiveRunSchema = z.object({
  runId: z.string(),
  /** The section week whose futures scored the step. */
  sectionWeek: z.number().int().positive(),
  incumbentMethodKey: z.string(),
  /** Eval run that scored the incumbent's live summaries. */
  liveEvalRunId: z.string().optional(),
  /** Eval run that scored the replays. */
  replayEvalRunId: z.string().optional(),
  numTuning: z.number().int().nonnegative(),
  numHeldOut: z.number().int().nonnegative(),
  /** Exemplar pool size after this step. */
  exemplarPoolSize: z.number().int().nonnegative().default(0),
  incumbentMeanScore: z.number().optional(),
  candidates: z.array(AdaptiveCandidateSchema),
  decision: z.enum(['pending', 'promoted', 'kept', 'failed']),
  /** Method key promoted, when `decision` is `promoted`. */
  promotedMethodKey: z.string().optional(),
  /** Who approved a promotion after the manual read. */
  approvedBy: z.string().optional(),
  status: z.enum(['running', 'done', 'failed']),
  startedAtMs: z.number(),
  finishedAtMs: z.number().optional(),
  costUsd: z.number().default(0),
  note: z.string().default(''),
});

/** One weekly adaptive step. */
export type AdaptiveRun = z.infer<typeof AdaptiveRunSchema>;

/**
 * Path of the adaptive runs collection.
 *
 * @param params.courseId The course.
 * @return The collection path.
 * @throws Never.
 */
export function getAdaptiveRunsPath({courseId}: {courseId: string}): string {
  return `summaryPipeline/${courseId}/adaptiveRuns`;
}

// ---------------------------------------------------------------------------
// The other type's summaries, written after section
// ---------------------------------------------------------------------------

/** One student's summary in the other type. */
export const CounterfactualStudentSchema = z.object({
  studentId: z.string(),
  summary: z.string(),
  /**
   * What the overview and PromptNCE read; absent for methods whose summary
   * is the representation.
   */
  representation: z.string().optional(),
  /** The live run whose activity window this summary reused. */
  sourceRunId: z.string(),
  /** End of that window, ISO. */
  windowEndIso: z.string(),
  costUsd: z.number().nullable().optional(),
  ledgerCallId: z.string().optional(),
  /** The per-student run doc this entry copies, in the pipeline's runs. */
  counterfactualRunId: z.string().optional(),
});

/** One student's summary in the other type. */
export type CounterfactualStudent = z.infer<typeof CounterfactualStudentSchema>;

/**
 * A section week's summaries in the type the section did not get, written
 * after the section met from the same activity windows, at
 * `sectionsummaries/{courseId}/sections/{sectionId}/counterfactual/{week}`.
 * Staff only until the debrief shows the section overview for the preference
 * question.
 */
export const CounterfactualSetSchema = z.object({
  sectionWeek: z.number().int().positive(),
  /** The type these summaries are written in (the section's other type). */
  summaryType: z.enum(SUMMARY_TYPES),
  methodKey: z.string(),
  students: z.array(CounterfactualStudentSchema),
  overallSummary: z.string().optional(),
  overviewPrompt: z.string().optional(),
  overviewCostUsd: z.number().nullable().optional(),
  generatedAtMs: z.number(),
  /** `overallGeneratedAtMs` of the live overview this one mirrors. */
  liveOverviewGeneratedAtMs: z.number().optional(),
  /**
   * Students in the live overview with no summary in this type (a failed or
   * over-budget run, or one still running when the overview was written).
   */
  missingStudentIds: z.array(z.string()).optional(),
});

/** A section week's summaries in the other type. */
export type CounterfactualSet = z.infer<typeof CounterfactualSetSchema>;

/**
 * Path of a section week's counterfactual doc.
 *
 * @param params.courseId The course.
 * @param params.sectionId The section.
 * @param params.sectionWeek 1-indexed section week.
 * @return The document path.
 * @throws Never.
 */
export function getCounterfactualPath({
  courseId,
  sectionId,
  sectionWeek,
}: {
  courseId: string;
  sectionId: string;
  sectionWeek: number;
}): string {
  return `sectionsummaries/${courseId}/sections/${sectionId}/counterfactual/${String(sectionWeek)}`;
}

/**
 * The type whose summaries a section's leader compares against after
 * section: B for type A sections, A for the recurrent types B and C.
 *
 * @param type The section's type.
 * @return The comparison type.
 * @throws Never.
 */
export function otherSummaryType(type: SummaryType): SummaryType {
  return type === 'A' ? 'B' : 'A';
}
